/**
 * PrisonerBadge — deterministic numbered-circle avatar in the style of
 * The Prisoner (1967). Used as a fallback when an operator has no
 * `google_picture` on their user record.
 *
 * Iter 31.76 — Six's directive 27 May 2026: "I miss the image pull from
 * logging in with LinkedIn it got lost, or a Gmail image, or a token
 * number image, a unique image for that user that's relevant."
 *
 * Design:
 *   - Pure SVG (no network, no LLM cost).
 *   - Deterministic per `seed` (user.id). Same seed → same badge forever.
 *   - Iconic Prisoner numbers (1, 2, 3, 6, 9, 11, 12, 14, 22, 48, 86) are
 *     biased into the picker so the badge feels canonically right rather
 *     than feeling like a random hash.
 *   - Two-tone palette derived from the hash — Spy Finance navy/gold by
 *     default, with a few alternate cues for variety.
 *   - Renders a stylised penny-farthing wheel behind the digit — the
 *     Number Six emblem from Village Hall — so the avatar reads as
 *     "operative", not "user 47b9".
 *
 * Special-case: Matt (the operator behind The Shed) is `1 / 6 / 9` in
 * canon; if the email matches one of his known addresses, force "6".
 */
import React, { useMemo } from "react";

// Iconic Prisoner numbers, in order of canon weight.
const ICONIC = [6, 2, 1, 9, 3, 11, 12, 14, 22, 48, 86, 7, 4, 8, 14, 24, 53, 58, 100];

// Matt-specific overrides — these accounts always render as No. 6.
const SIX_ACCOUNTS = new Set([
    "mjandrews@outlook.com",
    "emes.m.andrews@gmail.com",
    // Speed-login (`6`) hashed below also lands on 6, but defensively pin it.
    "6",
]);

const PALETTES = [
    { bg: "#0e1b30", fg: "#d4b56b", stroke: "#d4b56b" },   // navy + gold (Spy Finance house)
    { bg: "#1a1a1a", fg: "#f1ead8", stroke: "#f1ead8" },   // ink + paper
    { bg: "#5a1d1a", fg: "#f1ead8", stroke: "#d4b56b" },   // stamp-red + paper + gold
    { bg: "#214034", fg: "#d4b56b", stroke: "#d4b56b" },   // forest + gold
    { bg: "#3a2d1f", fg: "#f1ead8", stroke: "#d4b56b" },   // sepia + paper
];

// FNV-1a 32-bit hash — small, deterministic, no dependencies.
function hash32(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

export function avatarNumberForUser(user) {
    if (!user) return 6;
    const email = (user.email || "").toLowerCase();
    if (SIX_ACCOUNTS.has(email)) return 6;
    const seed = user.id || user.email || "";
    if (!seed) return 6;
    const h = hash32(seed);
    return ICONIC[h % ICONIC.length];
}

// Pull the first integer out of a Prisoner number string. Matt is
// canonical "1 / 6 / 9" — we surface "6" everywhere (his Village
// alias) rather than "1" (the leader). Falls back to 6 for malformed
// inputs so the SVG never explodes.
export function parsePrisonerNumber(raw) {
    if (raw === null || raw === undefined) return 6;
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return Math.max(0, Math.min(999, Math.trunc(raw)));
    }
    const s = String(raw);
    // Matt's composite number — Six is his Village number.
    if (/^\s*1\s*\/\s*6\s*\/\s*9\s*$/.test(s)) return 6;
    const m = s.match(/-?\d+/);
    if (!m) return 6;
    const n = parseInt(m[0], 10);
    if (Number.isNaN(n)) return 6;
    return Math.max(0, Math.min(999, n));
}

function paletteForUser(user) {
    const seed = (user && (user.id || user.email)) || "";
    const h = hash32("palette:" + seed);
    return PALETTES[h % PALETTES.length];
}

// Palette for a roster agent — keyed on agent_key so each agent always
// gets the same palette across renders, with a few canon-locked
// overrides for the named operatives.
function paletteForAgent(agent) {
    const key = (agent && (agent.key || agent.agent_key)) || "";
    // Canon-locked colour assignments
    if (key === "matt") return PALETTES[0];     // navy + gold — house operator
    if (key === "claude") return PALETTES[1];    // ink + paper — the handler
    if (key === "ern") return PALETTES[3];       // forest + gold — the builder
    if (!key) return PALETTES[0];
    const h = hash32("palette:agent:" + key);
    return PALETTES[h % PALETTES.length];
}

export default function PrisonerBadge({ user, agent, number, size = 36, title }) {
    // Resolution order: explicit `number` prop wins; otherwise derive
    // from an agent (canonical roster row) or fall back to the user hash.
    const resolvedNumber = useMemo(() => {
        if (number !== undefined && number !== null) return parsePrisonerNumber(number);
        if (agent) return parsePrisonerNumber(agent.number ?? agent.agent_number);
        return avatarNumberForUser(user);
    }, [number, agent, user]);
    const palette = useMemo(() => {
        if (agent) return paletteForAgent(agent);
        return paletteForUser(user);
    }, [agent, user]);
    const label = title
        || (agent && (agent.name || `No. ${resolvedNumber}`))
        || (user && user.name)
        || `No. ${resolvedNumber}`;
    // Bigger digits scale down a touch so two-digit numbers (No. 86, No. 48)
    // don't overflow the wheel hub. Three-digit edge case (e.g. No. 100)
    // gets a further pinch.
    const digits = String(resolvedNumber).length;
    const fontSize = digits >= 3 ? size * 0.32 : digits === 2 ? size * 0.42 : size * 0.55;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            width={size}
            height={size}
            role="img"
            aria-label={label}
            data-testid={`prisoner-badge-no-${resolvedNumber}`}
            style={{ display: "block", flex: "none" }}
        >
            <title>{label}</title>
            {/* Background disc — solid square frame to match the photo-ID pill style */}
            <rect x="0" y="0" width="64" height="64" fill={palette.bg} />
            {/* Penny-farthing big wheel — the Village emblem */}
            <circle
                cx="32" cy="32" r="26"
                fill="none" stroke={palette.stroke} strokeWidth="1.6"
                opacity="0.85"
            />
            {/* Spokes */}
            {[0, 30, 60, 90, 120, 150].map((deg) => (
                <line
                    key={deg}
                    x1="32" y1="6" x2="32" y2="58"
                    stroke={palette.stroke}
                    strokeWidth="0.8"
                    opacity="0.45"
                    transform={`rotate(${deg} 32 32)`}
                />
            ))}
            {/* Hub */}
            <circle cx="32" cy="32" r="14" fill={palette.bg} stroke={palette.stroke} strokeWidth="1.2" />
            {/* The number */}
            <text
                x="32" y="32"
                textAnchor="middle" dominantBaseline="central"
                fontFamily="'Special Elite', 'Courier New', Courier, monospace"
                fontWeight="700"
                fontSize={fontSize}
                fill={palette.fg}
                letterSpacing="0.5"
            >
                {resolvedNumber}
            </text>
            {/* Subtle "NO." marker so it reads as a Prisoner number, not a score */}
            <text
                x="32" y="56"
                textAnchor="middle"
                fontFamily="'Special Elite', 'Courier New', Courier, monospace"
                fontSize="5.5"
                fill={palette.stroke}
                letterSpacing="2"
                opacity="0.85"
            >
                NO.
            </text>
        </svg>
    );
}
